import { useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import PayDepositButton from "@/components/PayDepositButton";
import { SITE } from "@/constants/site";

const HOLIDAY_TYPES = {
  "holiday-dinner": "Holiday full dinner (15–20 guests)",
  "holiday-inhome": "In-home holiday cooking",
};

export default function Inquiries() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialInterest = HOLIDAY_TYPES[params.get("type")] || "";
  return (
    <div>
      <Seo
        title="Inquiries — Let's Build Your Night · Sita"
        description="Supper club seats, private events, catering, meal prep, or a personal chef — tell Sita what you have in mind. We respond within 48 hours."
      />

      {/* ---------- Heading ---------- */}
      <section data-testid="inquiries-hero" className="mx-auto max-w-7xl px-6 pb-12 pt-14 md:px-10 md:pb-16 md:pt-20">
        <div className="max-w-3xl">
          <FadeUp>
            <Overline>Work with us</Overline>
            <h1 className="mt-4 font-display text-5xl font-light text-forest md:text-6xl">Let's build your night.</h1>
            <GoldRule className="mt-8" />
            <p className="mt-8 text-lg leading-relaxed text-ink/85">
              Whether it is a seat at the table, a private event, catering, meal prep, or a personal chef, tell us what
              you have in mind. We respond within 48 hours.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ---------- Form ---------- */}
      <section data-testid="inquiries-form-section" className="mx-auto max-w-7xl px-6 pb-20 md:px-10 md:pb-28">
        <FadeUp delay={0.1}>
          <InquiryForm initialInterest={initialInterest} />
        </FadeUp>
      </section>

      {/* ---------- How booking works ---------- */}
      <section data-testid="inquiries-how-booking-works" className="bg-deepforest text-cream">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FadeUp>
                <Overline light>How booking works</Overline>
                <p className="mt-6 text-lg leading-relaxed text-cream/85">
                  You reach out. We talk it through. We send a custom proposal and a quote. A deposit holds your date.
                  Then we take care of the rest.
                </p>
                <p className="mt-6 text-sm leading-relaxed text-cream/60">
                  Looking for the monthly supper club? Seats are booked and paid through our{" "}
                  <a
                    href={SITE.stanStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="inquiries-stan-store-link"
                    className="underline decoration-gold underline-offset-4 hover:text-gold"
                  >
                    ticket page
                  </a>
                  . This form is for private events, catering, meal prep, and chef services.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.15} className="lg:col-span-5 lg:pl-8">
              <div className="rounded-3xl border border-cream/15 p-8">
                <p className="font-display text-2xl text-cream">Date confirmed with us?</p>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">
                  Hold it right now with a deposit — the balance is due {SITE.balanceDue} before your date.
                </p>
                <div className="mt-6">
                  <PayDepositButton />
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
